import { Link } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { SignalChip } from '@/components/shared/SignalChip';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO, breadcrumbSchema } from '@/components/shared/SEO';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const guideContent = {
  title: 'How to Use Meta Ads Library for Competitive Research',
  desc: 'A practical guide to using the Meta Ads Library effectively for monitoring competitor advertising strategies on Facebook and Instagram.',
  toc: [
    'What is Meta Ads Library',
    'How to search for competitor ads',
    'Analyzing ad creatives and copy',
    'Tracking campaign patterns',
    'Limitations of Meta Ads Library',
    'Why RADAR is better for ongoing monitoring',
  ],
  sections: [
    {
      heading: 'What is Meta Ads Library',
      body: 'The Meta Ads Library (formerly Facebook Ad Library) is a free, public database of all active ads running across Facebook and Instagram. It was created for advertising transparency and allows anyone to search for ads by advertiser name or keyword. While useful for quick lookups, it has significant limitations for serious competitive intelligence.',
    },
    {
      heading: 'How to search for competitor ads',
      body: 'To search the Meta Ads Library, visit ads.facebook.com/ads/library and select your country and ad category. Enter a competitor brand name or page to see all their active ads. You can filter by platform (Facebook or Instagram), media type, and date range. For best results, search by the exact Page name rather than keywords.',
    },
    {
      heading: 'Analyzing ad creatives and copy',
      body: 'When reviewing competitor ads, pay attention to several key elements: the hook in the first 3 seconds of video ads, the primary text and headline copy, the call to action, and the landing page URL. Look for patterns in messaging — are they focusing on price, features, social proof, or urgency? These patterns reveal their marketing strategy.',
    },
    {
      heading: 'Tracking campaign patterns',
      body: 'The most valuable insight from competitor ads is not individual creatives — it is patterns over time. Which ads run the longest? Those are likely performing well. Which formats do competitors keep testing? That shows where they see opportunity. RADAR automates this pattern detection across all your competitors simultaneously.',
    },
    {
      heading: 'Limitations of Meta Ads Library',
      body: 'While Meta Ads Library is a useful free resource, it has major limitations for serious competitive intelligence: no alerts or notifications when new ads launch, no way to track changes over time, no multi-competitor monitoring, no creative archiving, limited filtering and analysis tools, and no integration with other data sources like pricing or tech signals.',
    },
    {
      heading: 'Why RADAR is better for ongoing monitoring',
      body: 'RADAR builds on the transparency of the Meta Ads Library with powerful intelligence features designed for e-commerce operators. Instead of manually checking the Ad Library, RADAR continuously monitors your competitors and alerts you when they launch new campaigns, change strategies, or shift spending patterns. Combined with pricing, stock, and tech signal monitoring, RADAR gives you complete competitive visibility.',
    },
  ],
};

export default function HowToUseMetaAdsLibraryPage() {
  return (
    <MarketingLayout>
      <SEO
        title={guideContent.title}
        description={guideContent.desc}
        canonical="/guides/how-to-use-meta-ads-library"
        structuredData={breadcrumbSchema([
          { name: 'Home', href: '/' },
          { name: 'Resources', href: '/blog' },
          { name: 'How to Use Meta Ads Library', href: '/guides/how-to-use-meta-ads-library' },
        ])}
      />
      <Section hero>
        <Container>
          <AnimatedReveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
              <ArrowLeft className="h-4 w-4" /> Back to resources
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <SignalChip variant="lime">Guide</SignalChip>
              <SignalChip>Meta Ads</SignalChip>
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-foreground mb-4 max-w-[720px]">
              {guideContent.title}
            </h1>
            <p className="text-base text-muted-foreground max-w-[560px]">{guideContent.desc}</p>
          </AnimatedReveal>
        </Container>
      </Section>

      <Section dense>
        <Container>
          <div className="grid lg:grid-cols-4 gap-12">
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <GlassPanel className="p-5">
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-4">Contents</p>
                  <nav className="space-y-2">
                    {guideContent.toc.map((item, i) => (
                      <a
                        key={i}
                        href={`#section-${i}`}
                        className="block text-sm text-foreground/60 hover:text-lime-400 transition-colors py-1"
                      >
                        {item}
                      </a>
                    ))}
                  </nav>
                </GlassPanel>
              </div>
            </aside>

            <article className="lg:col-span-3 max-w-[720px] space-y-10">
              {guideContent.sections.map((s, i) => (
                <div key={i} id={`section-${i}`}>
                  <h2 className="text-xl font-display font-bold text-foreground mb-4">{s.heading}</h2>
                  <p className="text-sm text-muted-foreground leading-[1.8]">{s.body}</p>
                </div>
              ))}

              <div className="mt-12 pt-8 border-t border-white/[0.06]">
                <GlassPanel className="p-8 text-center">
                  <h3 className="text-lg font-display font-bold text-foreground mb-3">Go beyond the Ad Library</h3>
                  <p className="text-sm text-muted-foreground mb-6">RADAR automates competitive intelligence across Meta, TikTok, and Google.</p>
                  <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors">
                    Start free trial <ArrowRight className="h-4 w-4" />
                  </Link>
                </GlassPanel>
              </div>
            </article>
          </div>
        </Container>
      </Section>
    </MarketingLayout>
  );
}
